import {JsonObject, JsonObjectWithId, cloneJson} from 'utilities/types'
import {CAward, CBusiness, CConcept, CProject, CUser, INote, IUserFile} from '../'
import {CBaseRecord} from './_base'

export class CNote extends CBaseRecord<INote> implements INote {
  static record_name = 'note'
  static external_id = 'note_id'

  text
  is_public
  tags
  attachments
  graph_entity_id
  project_id
  tenant_id

  constructor(data: INote & JsonObject) {
    super(data)
    this.text = data.text
    this.is_public = data.is_public ?? false
    this.tags = data.tags
    this.attachments = data.attachments
    this.graph_entity_id = data.graph_entity_id
    this.project_id = data.project_id
    this.tenant_id = data.tenant_id
  }

  static parent(args: {
    entity_id?: string | null
    entity_type?: string | null
    project_id?: string | null
  }) {
    const {entity_id, entity_type, project_id} = args
    if (entity_id && entity_type) {
      switch (entity_type.toLowerCase()) {
        case 'award': {
          return {id: entity_id, recordClass: CAward, prop: 'notes'}
        }
        case 'business': {
          return {id: entity_id, recordClass: CBusiness, prop: 'notes'}
        }
        case 'concept': {
          return {id: entity_id, recordClass: CConcept, prop: 'notes'}
        }
        default: {
          throw new Error(
            `Notes are not currently supported on ${entity_type} nodes. Add an appropriate class and ApiRepo.`
          )
        }
      }
    }
    if (project_id) {
      return {id: project_id, recordClass: CProject, prop: 'notes'}
    }
    throw new Error(`Expected project_id or entity_type/entity_id, got: ${JSON.stringify(args)}`)
  }

  parent(entity_type?: string) {
    return CNote.parent({
      entity_id: this.graph_entity_id,
      entity_type,
      project_id: this.project_id,
    })
  }

  export() {
    return {
      id: this.id,
      text: this.text,
      tags: this.tags,
      created: cloneJson(this.created),
      updated: cloneJson(this.updated),
      attachments: cloneJson(this.attachments as (JsonObjectWithId & IUserFile)[] | undefined),
      graph_entity_id: this.graph_entity_id,
      project_id: this.project_id,
      tenant_id: this.tenant_id,
    }
  }

  static creatableBy(user?: CUser, project_id?: string, tenant_id?: string) {
    if (!user) {
      return false
    }
    if (project_id) {
      return user.hasProjectAccess({project_id, role: 'contributor'})
    }
    return user.hasTenantAccess({tenant_id, feature: 'note'})
  }

  editableBy(user?: CUser) {
    if (!user) {
      return false
    }
    if (user.is_global_admin || user.id === this.created?.by) {
      return true
    }
    if (this.project_id) {
      return user.hasProjectAccess({project_id: this.project_id, role: 'admin'})
    } else if (this.tenant_id) {
      return user.hasTenantAccess({
        tenant_id: this.tenant_id,
        require_admin: true,
        feature: 'note',
      })
    }
    return false
  }
}
